const toRadians = (value) => (value * Math.PI) / 180;

export const formatCoordinate = (point) => {
  if (!point) return "";
  const lat = Number(point.lat);
  const lng = Number(point.lng);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return "";
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

export const parseCoordinateValue = (value) => {
  if (!value || typeof value !== "string") return null;
  const [latText, lngText] = value.split(",").map((part) => part.trim());
  const lat = Number(latText);
  const lng = Number(lngText);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return { lat, lng };
};

export const haversineDistanceKm = (pointA, pointB) => {
  const earthRadiusKm = 6371;
  const latDelta = toRadians(pointB.lat - pointA.lat);
  const lngDelta = toRadians(pointB.lng - pointA.lng);
  const lat1 = toRadians(pointA.lat);
  const lat2 = toRadians(pointB.lat);

  const a =
    Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(lngDelta / 2) *
      Math.sin(lngDelta / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
};

export const calculateRouteDistanceKm = (points = []) => {
  if (!Array.isArray(points) || points.length < 2) return "";

  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    total += haversineDistanceKm(points[index - 1], points[index]);
  }

  return total.toFixed(2);
};

export const buildCoordinateOptions = (points = []) =>
  points
    .map((point) => formatCoordinate(point))
    .filter(Boolean);

export const getRouteCoordinatesSummary = (points = []) => {
  if (!points.length) return "";
  if (points.length === 1) return "1 coordinate point drawn";
  return `${points.length} coordinate points drawn`;
};

const projectPointOnSegment = (point, start, end) => {
  const dx = end.lng - start.lng;
  const dy = end.lat - start.lat;

  if (dx === 0 && dy === 0) {
    return { lat: start.lat, lng: start.lng };
  }

  const projection =
    ((point.lng - start.lng) * dx + (point.lat - start.lat) * dy) /
    (dx * dx + dy * dy);
  const t = Math.max(0, Math.min(1, projection));

  return {
    lat: start.lat + t * dy,
    lng: start.lng + t * dx,
  };
};

export const snapPointToRoute = (clickPoint, routePoints = []) => {
  const result = snapPointToRouteWithDistance(clickPoint, routePoints);
  return result?.point ?? clickPoint;
};

export const snapPointToRouteWithDistance = (clickPoint, routePoints = []) => {
  if (!routePoints.length) return null;
  if (routePoints.length === 1) {
    return {
      point: { lat: routePoints[0].lat, lng: routePoints[0].lng },
      distanceKm: haversineDistanceKm(clickPoint, routePoints[0]),
    };
  }

  let bestPoint = routePoints[0];
  let bestDistance = Infinity;

  for (let index = 0; index < routePoints.length - 1; index += 1) {
    const projected = projectPointOnSegment(
      clickPoint,
      routePoints[index],
      routePoints[index + 1]
    );
    const distance = haversineDistanceKm(clickPoint, projected);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestPoint = projected;
    }
  }

  return { point: bestPoint, distanceKm: bestDistance };
};

/** Keep stoppages on the drawn route; reject clicks that are too far away. */
export const resolveStoppagePointOnRoute = (
  clickPoint,
  routePoints = [],
  maxDistanceKm = 0.25
) => {
  if (!Array.isArray(routePoints) || routePoints.length < 2) {
    return {
      ok: false,
      reason: "no-route",
      message: "Draw route coordinates first before marking a stoppage.",
    };
  }

  const snapped = snapPointToRouteWithDistance(clickPoint, routePoints);
  if (!snapped?.point) {
    return {
      ok: false,
      reason: "no-route",
      message: "Unable to place stoppage on the route.",
    };
  }

  if (snapped.distanceKm > maxDistanceKm) {
    return {
      ok: false,
      reason: "too-far",
      message: "Please click on the drawn route line to mark a stoppage.",
      point: snapped.point,
      distanceKm: snapped.distanceKm,
    };
  }

  return {
    ok: true,
    point: snapped.point,
    distanceKm: snapped.distanceKm,
  };
};

export const buildStoppageDisplayName = (stoppageCode, locationName) => {
  const code = stoppageCode?.trim() ?? "";
  const location = locationName?.trim() ?? "";

  if (code && location) return `${code}, ${location}`;
  return code || location || "";
};

export const formatStoppageLatLng = (lat, lng) => {
  if (lat === "" || lng === "" || lat == null || lng == null) return "";
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return "";
  return `${longitude}, ${latitude}`;
};

export const parseStoppageLatLng = (value) => {
  if (!value || typeof value !== "string") return null;

  const [firstText, secondText] = value.split(",").map((part) => part.trim());
  const first = Number(firstText);
  const second = Number(secondText);
  if (Number.isNaN(first) || Number.isNaN(second)) return null;

  const isLatFirst = Math.abs(first) <= 90 && Math.abs(second) <= 180;
  const isLngFirst = Math.abs(first) <= 180 && Math.abs(second) <= 90;

  if (isLngFirst && !isLatFirst) {
    return { lat: second, lng: first };
  }

  return { lat: first, lng: second };
};

export const formatTimeFromIso = (value) => {
  if (!value) return "";

  const text = String(value).trim();
  if (/^\d{1,2}:\d{2}$/.test(text)) return text;

  const parsed = parseStoppageDateTime(text);
  if (!parsed) return "";

  return `${String(parsed.getHours()).padStart(2, "0")}:${String(
    parsed.getMinutes()
  ).padStart(2, "0")}`;
};

/** Parse API formats: DD-MM-YYYY HH:mm:ss, ISO, YYYY-MM-DDTHH:mm, HH:mm */
export const parseStoppageDateTime = (value) => {
  if (!value) return null;

  const text = String(value).trim();
  if (!text) return null;

  const dmyMatch = text.match(
    /^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (dmyMatch) {
    const day = Number(dmyMatch[1]);
    const month = Number(dmyMatch[2]) - 1;
    const year = Number(dmyMatch[3]);
    const hours = Number(dmyMatch[4] ?? 0);
    const minutes = Number(dmyMatch[5] ?? 0);
    const seconds = Number(dmyMatch[6] ?? 0);
    const date = new Date(year, month, day, hours, minutes, seconds);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const timeOnly = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (timeOnly) {
    const date = new Date();
    date.setHours(
      Number(timeOnly[1]),
      Number(timeOnly[2]),
      Number(timeOnly[3] ?? 0),
      0
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Value for <input type="datetime-local" /> → YYYY-MM-DDTHH:mm */
export const formatStoppageDateTimeForInput = (value) => {
  const date = parseStoppageDateTime(value);
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/** Display like API: DD-MM-YYYY HH:mm:ss */
export const formatStoppageDateTimeDisplay = (value) => {
  const date = parseStoppageDateTime(value);
  if (!date) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

export const splitTimeToHourMinute = (value) => {
  const formatted = formatTimeFromIso(value);
  if (!formatted) return { hours: "", minutes: "" };

  const [hours, minutes] = formatted.split(":");
  return { hours, minutes };
};

export const joinHourMinuteToTime = (hours, minutes) => {
  const hourText = String(hours ?? "").trim();
  const minuteText = String(minutes ?? "").trim();

  if (!hourText && !minuteText) return "";
  if (!hourText || !minuteText) return null;

  const hoursNum = Number(hourText);
  const minutesNum = Number(minuteText);
  if (
    Number.isNaN(hoursNum) ||
    Number.isNaN(minutesNum) ||
    hoursNum < 0 ||
    hoursNum > 23 ||
    minutesNum < 0 ||
    minutesNum > 59
  ) {
    return null;
  }

  return `${String(hoursNum).padStart(2, "0")}:${String(minutesNum).padStart(2, "0")}`;
};

export const toIsoDateTimeFromTime = (timeValue) => {
  if (!timeValue && timeValue !== 0) return null;

  const text = String(timeValue).trim();
  if (!text) return null;

  const parsed = parseStoppageDateTime(text);
  if (!parsed) return null;

  return parsed.toISOString();
};

/** Sends time only (HH:mm:ss) — no date. Accepts HH:mm, HH:mm:ss, or ISO datetime. */
export const toTimeOnlyValue = (timeValue) => {
  if (!timeValue && timeValue !== 0) return null;

  const text = String(timeValue).trim();
  if (!text) return null;

  const hhmmss = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (hhmmss) {
    const hours = Number(hhmmss[1]);
    const minutes = Number(hhmmss[2]);
    const seconds = Number(hhmmss[3] ?? 0);
    if (hours > 23 || minutes > 59 || seconds > 59) return null;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  const formatted = formatTimeFromIso(text);
  if (!formatted) return null;

  const [hours, minutes] = formatted.split(":");
  return `${hours}:${minutes}:00`;
};

export const buildSaveRouteStoppagePayload = ({
  stoppage = {},
  routeId = 0,
  sequenceNumber = 1,
} = {}) => {
  const name = stoppage.name?.trim() || "";
  const locationName = stoppage.locationName?.trim() || name;
  let stoppageCode = stoppage.stoppageCode?.trim() || "";
  const sequence = Number(sequenceNumber) || 1;
  const existingStoppageId = Number(stoppage.stoppageId) || 0;

  if (!stoppageCode && name.includes(",")) {
    stoppageCode = name.split(",")[0].trim();
  }

  return {
    // New stoppages send 0; existing stoppages keep their stoppageId.
    stoppageId: existingStoppageId > 0 ? existingStoppageId : 0,
    routeId: Number(routeId) || 0,
    stoppageCode,
    locationName,
    latitude: String(stoppage.latitude ?? ""),
    longitude: String(stoppage.longitude ?? ""),
    arrivalTime: toTimeOnlyValue(stoppage.arrivalTime),
    departureTime: toTimeOnlyValue(stoppage.departureTime),
    name,
    sequenceNumber: sequence,
  };
};

export const createEmptyStoppage = (id = Date.now()) => ({
  id,
  stoppageId: 0,
  name: "",
  stoppageCode: "",
  locationName: "",
  latitude: "",
  longitude: "",
  arrivalTime: "",
  departureTime: "",
});

export const parseRouteCoordinatesFromDetail = (item = {}) => {
  const source = {
    ...item,
    ...(item?.routeMaster && typeof item.routeMaster === "object"
      ? item.routeMaster
      : {}),
    ...(item?.routeDetails && typeof item.routeDetails === "object"
      ? item.routeDetails
      : {}),
    ...(item?.data && typeof item.data === "object" && !Array.isArray(item.data)
      ? item.data
      : {}),
  };

  const toPoint = (point) => {
    if (!point) return null;

    if (typeof point === "string") {
      return parseCoordinateValue(point);
    }

    const lat = Number(
      point.lat ??
        point.latitude ??
        point.Latitude ??
        point.y ??
        point.Y
    );
    const lng = Number(
      point.lng ??
        point.longitude ??
        point.Longitude ??
        point.x ??
        point.X
    );

    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  };

  const normalizePoints = (points = []) =>
    points.map(toPoint).filter(Boolean);

  const candidateLists = [
    source.coordinates,
    source.routeCoordinates,
    source.routeCoordinateList,
    source.routePath,
    source.routePoints,
    source.collectionPointMapDetails,
    source.collectionPoints,
    item.coordinates,
    item.routeCoordinates,
  ];

  for (const list of candidateLists) {
    if (!Array.isArray(list) || !list.length) continue;

    const hasSequence = list.some(
      (point) =>
        point?.sequenceNumber !== undefined || point?.sequence !== undefined
    );

    const ordered = hasSequence
      ? [...list].sort(
          (a, b) =>
            (a.sequenceNumber ?? a.sequence ?? 0) -
            (b.sequenceNumber ?? b.sequence ?? 0)
        )
      : list;

    const points = normalizePoints(ordered);
    if (points.length) return points;
  }

  if (typeof source.routeCoordinates === "string" && source.routeCoordinates.trim()) {
    try {
      const parsed = JSON.parse(source.routeCoordinates);
      const points = normalizePoints(Array.isArray(parsed) ? parsed : [parsed]);
      if (points.length) return points;
    } catch {
      const point = parseCoordinateValue(source.routeCoordinates);
      if (point) return [point];
    }
  }

  const start = parseCoordinateValue(
    source.startPointCoordinate ??
      source.startCoordinate ??
      source.startPointLatLng
  );
  const end = parseCoordinateValue(
    source.endPointCoordinate ?? source.endCoordinate ?? source.endPointLatLng
  );

  if (start && end) return [start, end];
  if (start) return [start];

  return [];
};

export const parseStoppagesFromDetail = (item = {}) => {
  const rawStoppages =
    item.stoppages ??
    item.routeStoppages ??
    item.routeStoppagePoints ??
    item.stoppagePoints ??
    [];

  if (!Array.isArray(rawStoppages)) return [];

  return rawStoppages.map((stoppage, index) => ({
    id: stoppage.id ?? stoppage.stoppageId ?? index + 1,
    stoppageId: stoppage.stoppageId ?? 0,
    name:
      stoppage.name ??
      stoppage.stoppagePoint ??
      stoppage.stoppageName ??
      stoppage.locationName ??
      "",
    stoppageCode: stoppage.stoppageCode ?? "",
    locationName: stoppage.locationName ?? "",
    latitude: stoppage.latitude ?? stoppage.lat ?? "",
    longitude: stoppage.longitude ?? stoppage.lng ?? "",
    arrivalTime: stoppage.arrivalTime ?? "",
    departureTime: stoppage.departureTime ?? "",
    sequence: stoppage.sequence ?? stoppage.sequenceNumber ?? index + 1,
  }));
};

export const parseRouteStoppagesResponse = (response) => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) {
    return parseStoppagesFromDetail({ stoppages: payload });
  }

  if (Array.isArray(payload?.data)) {
    return parseStoppagesFromDetail({ stoppages: payload.data });
  }

  if (Array.isArray(payload?.stoppages)) {
    return parseStoppagesFromDetail({ stoppages: payload.stoppages });
  }

  return parseStoppagesFromDetail(payload);
};

export const reverseGeocode = async (lat, lng) => {
  const googleMaps = window.google?.maps;
  if (googleMaps?.Geocoder) {
    const geocoder = new googleMaps.Geocoder();
    const result = await new Promise((resolve) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results?.length) {
          resolve(results[0]?.formatted_address ?? "");
        } else {
          resolve("");
        }
      });
    });
    if (result) return result;
  }

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        lat
      )}&lon=${encodeURIComponent(lng)}`,
      { headers: { Accept: "application/json" } }
    );
    const json = await response.json();
    return json?.display_name ?? "";
  } catch {
    return "";
  }
};

export const resolvePlaceNameFromCoordinate = async (coordinateValue) => {
  const point = parseCoordinateValue(coordinateValue);
  if (!point) return coordinateValue ?? "";

  const placeName = await reverseGeocode(point.lat, point.lng);
  return placeName || formatCoordinate(point);
};

/** Normalize master API responses to a plain array. */
export const unwrapMasterList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.result)) return response.result;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

/** Normalize API/display dates to YYYY-MM-DD for <input type="date" />. */
export const toDateInputValue = (dateValue) => {
  if (!dateValue || dateValue === "-") return "";

  const text = String(dateValue).trim();
  const yyyyMmDd = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (yyyyMmDd) {
    return `${yyyyMmDd[1]}-${yyyyMmDd[2]}-${yyyyMmDd[3]}`;
  }

  const ddMmYyyy = text.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (ddMmYyyy) {
    return `${ddMmYyyy[3]}-${ddMmYyyy[2]}-${ddMmYyyy[1]}`;
  }

  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const toIsoDateTime = (dateValue) => {
  if (!dateValue) return null;

  const text = String(dateValue).trim();
  if (!text) return null;

  // Date-only from <input type="date"> — keep calendar day (avoid UTC shift).
  const yyyyMmDd = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (yyyyMmDd) {
    return `${yyyyMmDd[1]}-${yyyyMmDd[2]}-${yyyyMmDd[3]}T00:00:00`;
  }

  // DD-MM-YYYY (with optional time)
  const ddMmYyyy = text.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (ddMmYyyy) {
    return `${ddMmYyyy[3]}-${ddMmYyyy[2]}-${ddMmYyyy[1]}T00:00:00`;
  }

  // ISO / datetime-local — use local date parts, not toISOString() UTC.
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}T00:00:00`;
};

export const buildAddRouteDetailsPayload = (payload = {}) => {
  const routeCoordinates = Array.isArray(payload.routeCoordinates)
    ? payload.routeCoordinates
    : [];
  const filledStoppages = Array.isArray(payload.stoppages) ? payload.stoppages : [];
  const routeId = Number(payload.routeId) || 0;

  const coordinates = routeCoordinates.map((point, index) => ({
    coordinateId: index + 1,
    latitude: Number(point.lat),
    longitude: Number(point.lng),
    sequenceNumber: index + 1,
  }));

  const stoppages = filledStoppages.map((stoppage, index) => ({
    stoppageId: Number(stoppage.stoppageId) || 0,
    routeId,
    stoppageCode: stoppage.stoppageCode?.trim() || "",
    locationName:
      stoppage.locationName?.trim() ||
      stoppage.name?.trim() ||
      "",
    latitude: String(stoppage.latitude ?? ""),
    longitude: String(stoppage.longitude ?? ""),
    arrivalTime: toTimeOnlyValue(stoppage.arrivalTime),
    departureTime: toTimeOnlyValue(stoppage.departureTime),
    name: stoppage.name?.trim() || "",
    sequenceNumber: stoppage.sequence ?? stoppage.sequenceNumber ?? index + 1,
  }));

  return {
    routeMaster: {
      routeCode: payload.routeCode?.trim() || "",
      routeName:
        payload.routeName?.trim() ||
        payload.routeDescription?.trim() ||
        "",
      zoneId: Number(payload.zoneId ?? payload.applicableZoneId) || 0,
      wardId:
        Number(
          payload.wardId ??
            payload.applicableWardId ??
            payload.circleId
        ) || 0,
      applicableColonyId: String(payload.applicableColonyId ?? ""),
      applicableDurationToDate: toIsoDateTime(payload.applicableDurationToDate),
      applicableLocation: payload.applicableLocation?.trim() || "",
      applicableMts: payload.applicableMts || "",
      applicableRecyclingCenter: payload.applicableRecyclingCenter || "",
      applicableVehicleTypeId: String(payload.applicableVehicleTypeId ?? ""),
      applicableZoneId: String(payload.applicableZoneId ?? payload.zoneId ?? ""),
      endPointCoordinate: payload.endPointCoordinate || "",
      routeApplicableFor: payload.routeApplicableFor || "",
      routeDescription: payload.routeDescription?.trim() || "",
      routeType: payload.routeType || "",
      startPointCoordinate: payload.startPointCoordinate || "",
      totalDistanceKm: String(payload.totalDistanceKm ?? ""),
    },
    stoppages,
    coordinates,
  };
};

export const resolveRouteEndPlaceNames = async (points = []) => {
  if (!points.length) {
    return { startPoint: "", endPoint: "" };
  }

  const start = points[0];
  const end = points[points.length - 1];

  const [startPoint, endPoint] = await Promise.all([
    reverseGeocode(start.lat, start.lng),
    reverseGeocode(end.lat, end.lng),
  ]);

  return {
    startPoint: startPoint || formatCoordinate(start),
    endPoint: endPoint || formatCoordinate(end),
  };
};
